// hooks/useErrorHandler.js
import { useEffect } from "react";

const useErrorHandler = () => {
  useEffect(() => {
    // Original console methods
    const originalConsoleError = console.error;
    const originalConsoleWarn = console.warn;

    // Utility: Extract file & line from stack trace
    const extractFileInfo = (stack) => {
      if (!stack) return { file: "unknown", line: "unknown" };
      const stackLines = stack.split("\n");
      const relevantLine = stackLines[1] || stackLines[0]; // second line usually has the caller
      const match = relevantLine.match(/(?:\()?(.*?):(\d+):\d+\)?$/);
      if (match) {
        return { file: match[1], line: match[2] };
      }
      return { file: "unknown", line: "unknown" };
    };

    // Override console.error
    console.error = (...args) => {
      const message = args.join(" ");
      const suppressedErrors = [
        "Cannot set properties of undefined (setting 'toggle')",
      ];
      const shouldSuppress = suppressedErrors.some((pattern) =>
        message.includes(pattern)
      );

      if (!shouldSuppress) {
        const stack = new Error().stack;
        const { file, line } = extractFileInfo(stack);
        originalConsoleError(
          `%c[ERROR] File: ${file}, Line: ${line}\nMessage: ${message}`,
          "color: red; font-weight: bold;"
        );
        originalConsoleError("Stack Trace:", stack);
      }
    };

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event) => {
      const stack = event.reason?.stack || "";
      const { file, line } = extractFileInfo(stack);
      originalConsoleError(
        `%c[UNHANDLED PROMISE REJECTION] File: ${file}, Line: ${line}\nReason: ${event.reason}`,
        "color: orange; font-weight: bold;"
      );
      originalConsoleError("Stack Trace:", stack);
    };

    // Handle global errors
    const handleError = (event) => {
      const { filename, lineno, message } = event;
      originalConsoleError(
        `%c[GLOBAL ERROR] File: ${filename || "unknown"}, Line: ${
          lineno || "unknown"
        }\nMessage: ${message}`,
        "color: purple; font-weight: bold;"
      );
      originalConsoleError("Stack Trace:", event.error?.stack || "N/A");
    };

    // Add event listeners
    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    // Cleanup
    return () => {
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
      window.removeEventListener("error", handleError);
    };
  }, []);
};

export default useErrorHandler;
